import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtCurrency, fmtDate } from "@/lib/constants";
import { Plus, X, Trash2, Pencil, FileDown, Sparkles, Loader2, Eye, Copy, ChevronDown, FileText, ReceiptText } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";
import { QuotePreviewModal } from "@/components/QuotePreview";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft:    "bg-slate-500/10 text-slate-500",
  sent:     "bg-blue-500/10 text-blue-500",
  accepted: "bg-emerald-500/10 text-emerald-500",
  rejected: "bg-red-500/10 text-red-500",
  expired:  "bg-orange-500/10 text-orange-500",
};

const EMPTY_ITEM = { description: "", quantity: 1, unit: "Nos", unit_price: 0, discount_percent: 0, tax_percent: 18 };

export default function Quotations() {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null); // AI-imported draft
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const importRef = useRef(null);

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [r, c] = await Promise.all([api.get(`/quotations${q}`), api.get("/customers")]);
      setQuotes(r.data); setCustomers(c.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/quotations/parse-file", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setDraft(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const ids = quotes.map((q) => q.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} quotation(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/quotations/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  const filters = [["", "All"], ["draft","Draft"], ["sent","Sent"], ["accepted","Accepted"], ["rejected","Rejected"], ["expired","Expired"]];

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Sales</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Quotations</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create quotes manually or import from PDF/Word, then download a branded Word file.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={importRef} type="file" accept=".pdf,.docx" onChange={onImportFile} className="hidden" data-testid="quote-import-input" />
          <button data-testid="quote-import-button" disabled={importing} onClick={() => importRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/50 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-60">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {importing ? "Reading file…" : "Import PDF/Word"}
          </button>
          <button data-testid="quote-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New quotation
          </button>
        </div>
      </header>

      {err && <div data-testid="quote-error" className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
      {msg && (
        <div data-testid="quote-success" className="mb-3 flex items-center justify-between rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">
          <span>{msg}</span>
          <button onClick={() => setMsg("")} className="ml-3 text-emerald-600/70 hover:text-emerald-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {importing && (
        <div className="mb-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary">
          AI is reading your document and extracting items — this takes a few seconds…
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map(([v, l]) => (
          <button key={v || "all"} data-testid={`quote-filter-${v || "all"}`} onClick={() => setStatusFilter(v)}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${statusFilter === v ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="quote-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Quote #</th>
              <th className="px-4 py-2 label-eyebrow">Customer</th>
              <th className="px-4 py-2 label-eyebrow">Subject</th>
              <th className="px-4 py-2 label-eyebrow">Total</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Valid until</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                <td className="px-4 py-2">
                  <RowCheckbox testid={`quote-select-${q.id}`} checked={selected.has(q.id)} onChange={() => toggleOne(q.id)} />
                </td>
                <td className="px-4 py-2 font-mono text-xs">{q.quote_no}</td>
                <td className="px-4 py-2">{q.customer_name || "—"}</td>
                <td className="max-w-[220px] truncate px-4 py-2 text-xs text-muted-foreground" title={q.subject || ""}>{q.subject || "—"}</td>
                <td className="px-4 py-2 font-semibold">{fmtCurrency(q.total)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[q.status] || STATUS_STYLES.draft}`}>{q.status}</span>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(q.valid_until)}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`quote-preview-${q.id}`} title="Preview quotation"
                      onClick={() => setPreview(q)}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Eye className="h-3.5 w-3.5" /></button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`quote-download-${q.id}`} title="Download (choose format)"
                          className="inline-flex items-center gap-0.5 rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary">
                          <FileDown className="h-3.5 w-3.5" /><ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`quote-docx-${q.id}`}
                          onClick={() => window.open(`${BACKEND_URL}/api/quotations/${q.id}/docx`, "_blank")}>
                          <FileDown className="mr-2 h-3.5 w-3.5" /> Word (.docx)
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`quote-pdf-${q.id}`}
                          onClick={() => window.open(`${BACKEND_URL}/api/quotations/${q.id}/pdf`, "_blank")}>
                          <FileText className="mr-2 h-3.5 w-3.5" /> PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <button data-testid={`quote-to-invoice-${q.id}`}
                      title={q.invoice_no ? `Already converted to ${q.invoice_no}` : "Convert to invoice"}
                      disabled={!!q.invoice_no}
                      onClick={async () => {
                        if (!window.confirm(`Convert ${q.quote_no} to an invoice?`)) return;
                        try {
                          const r = await api.post(`/quotations/${q.id}/to-invoice`);
                          setMsg(`Invoice ${r.data.invoice_no} created from ${q.quote_no} — see Billing → Invoices.`);
                          await load();
                        } catch (e) { setErr(formatApiError(e)); }
                      }}
                      className={`rounded-md border p-1.5 ${q.invoice_no ? "border-emerald-500/40 text-emerald-500 opacity-70" : "border-border/70 text-muted-foreground hover:text-emerald-500"}`}>
                      <ReceiptText className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`quote-duplicate-${q.id}`} title="Duplicate quotation"
                      onClick={async () => { try { await api.post(`/quotations/${q.id}/duplicate`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Copy className="h-3.5 w-3.5" /></button>
                    <button data-testid={`quote-edit-${q.id}`} onClick={() => setEditing(q)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`quote-delete-${q.id}`}
                      onClick={async () => { if (!window.confirm(`Delete quotation ${q.quote_no}?`)) return; try { await api.delete(`/quotations/${q.id}`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {quotes.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-muted-foreground">No quotations yet — create one or import a PDF/Word quote.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="quotations" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <QuoteModal customers={customers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <QuoteModal existing={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {draft && <QuoteModal draft={draft} customers={customers} onClose={() => setDraft(null)} onSaved={() => { setDraft(null); load(); }} />}
      {preview && <QuotePreviewModal quote={preview} onClose={() => { setPreview(null); load(); }} />}
    </div>
  );
}

function QuoteModal({ customers, onClose, onSaved, existing, draft }) {
  const base = existing || draft || {};
  const [form, setForm] = useState({
    customer_id: base.customer_id || "",
    customer_name: base.customer_name || "",
    quote_no: base.quote_no || "",
    subject: base.subject || "",
    valid_until: base.valid_until || "",
    status: base.status || "draft",
    notes: base.notes || "",
    terms: base.terms || "",
  });
  const [items, setItems] = useState(base.items?.length ? base.items.map((i) => ({ ...EMPTY_ITEM, ...i })) : [{ ...EMPTY_ITEM }]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const totals = items.reduce((acc, i) => {
    const line = (Number(i.quantity) || 0) * (Number(i.unit_price) || 0);
    const disc = line * (Number(i.discount_percent) || 0) / 100;
    const tax = (line - disc) * (Number(i.tax_percent) || 0) / 100;
    acc.sub += line; acc.disc += disc; acc.tax += tax; return acc;
  }, { sub: 0, disc: 0, tax: 0 });
  const grand = totals.sub - totals.disc + totals.tax;

  const addRow = () => setItems([...items, { ...EMPTY_ITEM }]);
  const rmRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 0,
          unit: i.unit || "Nos",
          unit_price: Number(i.unit_price) || 0,
          discount_percent: Number(i.discount_percent) || 0,
          tax_percent: Number(i.tax_percent) || 0,
        })),
      };
      if (!payload.quote_no) delete payload.quote_no;
      if (!existing && !payload.customer_id) delete payload.customer_id;
      if (existing) await api.patch(`/quotations/${existing.id}`, payload);
      else await api.post("/quotations", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl sharp-card max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3 sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            {draft && <Sparkles className="h-4 w-4 text-primary" />}
            {existing ? `Edit ${existing.quote_no}` : draft ? `Review imported quote${draft.source_file ? ` — ${draft.source_file}` : ""}` : "New quotation"}
          </h3>
          <button data-testid="quote-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-4 p-5">
          {draft && (
            <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary">
              AI extracted this draft from your file — review every field, then save.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label-eyebrow mb-1.5 block">Customer {form.customer_id ? "" : "(or type a name below)"}</label>
              <select data-testid="quote-form-customer" value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">— Not in customer list —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              {!form.customer_id && (
                <input data-testid="quote-form-customer-name" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Customer / company name" required
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow mb-1.5 block">Quote # (auto if blank)</label>
                <input data-testid="quote-form-no" value={form.quote_no} onChange={(e) => setForm({ ...form, quote_no: e.target.value })} placeholder="QT-2026-0001" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="label-eyebrow mb-1.5 block">Valid until</label>
                <input data-testid="quote-form-valid" type="date" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow mb-1.5 block">Status</label>
                <select data-testid="quote-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {["draft","sent","accepted","rejected","expired"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">Subject</label>
            <input data-testid="quote-form-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Supply & installation of CCTV system" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="label-eyebrow">Line items</p>
              <button type="button" data-testid="quote-add-row" onClick={addRow} className="text-xs text-primary hover:underline">+ Add row</button>
            </div>
            <div className="overflow-x-auto rounded-md border border-border/60">
              <div className="grid min-w-[760px] grid-cols-[1fr_70px_70px_100px_70px_70px_110px_36px] gap-2 border-b border-border/60 bg-secondary/30 p-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <span>Description</span><span>Qty</span><span>Unit</span><span>Rate</span><span>Disc %</span><span>Tax %</span><span className="text-right">Amount</span><span />
              </div>
              {items.map((it, i) => {
                const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                const amt = (line - line * (Number(it.discount_percent) || 0) / 100) * (1 + (Number(it.tax_percent) || 0) / 100);
                return (
                  <div key={i} className="grid min-w-[760px] grid-cols-[1fr_70px_70px_100px_70px_70px_110px_36px] items-center gap-2 border-b border-border/40 p-2 last:border-0">
                    <input data-testid={`quote-item-desc-${i}`} placeholder="Description" required value={it.description} onChange={(e) => setRow(i, "description", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <input value={it.unit} onChange={(e) => setRow(i, "unit", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setRow(i, "unit_price", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <input type="number" min="0" max="100" step="0.01" value={it.discount_percent} onChange={(e) => setRow(i, "discount_percent", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <input type="number" min="0" step="0.01" value={it.tax_percent} onChange={(e) => setRow(i, "tax_percent", e.target.value)} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <span className="text-right text-xs font-semibold">{fmtCurrency(amt)}</span>
                    <button type="button" onClick={() => rmRow(i)} className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-destructive"><X className="mx-auto h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex justify-end">
              <div className="w-64 rounded-md border border-border/60 bg-secondary/30 p-3 text-xs">
                <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{fmtCurrency(totals.sub)}</span></div>
                {totals.disc > 0 && <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Discount</span><span className="font-semibold text-emerald-500">- {fmtCurrency(totals.disc)}</span></div>}
                <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Tax (GST)</span><span className="font-semibold">{fmtCurrency(totals.tax)}</span></div>
                <div className="mt-1 flex justify-between border-t border-border/60 pt-1.5 font-heading text-sm"><span>Grand Total</span><span data-testid="quote-grand-total" className="font-bold text-primary">{fmtCurrency(grand)}</span></div>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label-eyebrow mb-1.5 block">Notes</label>
              <textarea data-testid="quote-form-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Terms &amp; conditions (blank = standard terms)</label>
              <textarea data-testid="quote-form-terms" value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={3} placeholder={"1. Payment: 100% against delivery\n2. Delivery: 1-2 weeks from PO"} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>

          {err && <div data-testid="quote-form-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="quote-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create quotation")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
